import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  FileText, Shield, ChevronRight, CheckCircle2, Star, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const CATEGORY_COLORS = {
  'Governance': 'bg-[#D4A843]/10 text-[#D4A843]',
  'Security': 'bg-red-100 text-red-700',
  'Risk': 'bg-orange-100 text-orange-700',
  'Operations': 'bg-emerald-100 text-emerald-700',
  'Privacy': 'bg-purple-100 text-purple-700',
  'Resilience': 'bg-blue-100 text-blue-700',
  'Training': 'bg-amber-100 text-amber-700',
  'Strategy': 'bg-teal-100 text-teal-700',
  'Compliance': 'bg-indigo-100 text-indigo-700',
  'AI & Emerging': 'bg-pink-100 text-pink-700',
};

export default function StorePage() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filterCat, setFilterCat] = useState('');

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/api/docforge/templates'); setTemplates(data.templates || []); } catch (e) { /* handled */ }
    })();
  }, []);

  const categories = [...new Set(templates.map(t => t.category))];
  const filtered = filterCat ? templates.filter(t => t.category === filterCat) : templates;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="store-page">
      {/* Hero */}
      <div className="bg-[#0F172A] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#D4A843] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold tracking-tight">TechOps DocForge</h1>
              <p className="text-sm text-gray-400">by EIGOO Inc | eigooinc.net</p>
            </div>
          </div>
          <p className="text-lg text-gray-300 max-w-2xl mt-4">AI-powered compliance, governance, and security documentation. Professional-grade documents generated in minutes, not days.</p>
          <div className="flex items-center gap-6 mt-6">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-sm text-gray-300">12 Document Types</span></div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-sm text-gray-300">AI-Generated</span></div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-sm text-gray-300">Industry-Specific</span></div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Category Filter */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          <button onClick={() => setFilterCat('')} className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${!filterCat ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-testid="store-filter-all">All Templates</button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilterCat(cat)} className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${filterCat === cat ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-testid={`store-filter-${cat}`}>{cat}</button>
          ))}
        </div>

        {/* Template Detail */}
        {selected && (
          <div className="mb-10 bg-white border border-gray-200 rounded-xl p-8 animate-fade-in" data-testid="store-template-detail">
            <button onClick={() => setSelected(null)} className="text-xs text-[#D4A843] hover:underline mb-4 block">Back to all templates</button>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 bg-[#D4A843]/10 rounded-xl flex items-center justify-center shrink-0"><FileText className="w-7 h-7 text-[#D4A843]" /></div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selected.title}</h2>
                <p className="text-sm text-gray-500 mt-1">{selected.description}</p>
              </div>
            </div>

            {/* Pricing Tiers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                { tier: 'basic', desc: 'Template-based document with standard language. 1 revision, 2-day delivery.', features: ['Standard template', '1 revision', 'PDF delivery'] },
                { tier: 'standard', desc: 'Customized to your business. Specific recommendations and actionable items. 2 revisions, 3-day delivery.', features: ['Customized content', '2 revisions', 'Industry-specific', 'Actionable items'] },
                { tier: 'premium', desc: 'Executive-grade package. Strategy roadmap, executive deck, appendices. 3 revisions, 4-day delivery.', features: ['Everything in Standard', '3 revisions', 'Strategy roadmap', 'Executive summary', 'Implementation plan'] },
              ].map(t => (
                <div key={t.tier} className={`border-2 rounded-xl p-6 ${t.tier === 'standard' ? 'border-[#D4A843] ring-2 ring-[#D4A843]/10' : 'border-gray-200'}`}>
                  {t.tier === 'standard' && <span className="text-[10px] uppercase tracking-wider font-bold text-[#D4A843] bg-[#D4A843]/10 px-2 py-0.5 rounded mb-2 inline-block">Most Popular</span>}
                  <p className="text-sm font-bold text-gray-900 uppercase">{t.tier}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">${selected.pricing[t.tier]}</p>
                  <p className="text-xs text-gray-500 mt-2 mb-4">{t.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {t.features.map((f, i) => <li key={`item-${i}`} className="flex items-center gap-2 text-xs text-gray-600"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />{f}</li>)}
                  </ul>
                  <a href="/login">
                    <Button className={`w-full rounded-lg text-xs ${t.tier === 'standard' ? 'bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid={`store-buy-${t.tier}`}>
                      Get Started <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </a>
                </div>
              ))}
            </div>

            {/* Intake Preview */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3">What We'll Need From You</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {selected.intake_fields.map(f => (
                  <div key={f.id} className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs font-medium text-gray-900">{f.label}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{f.type === 'multiselect' ? 'Multiple choice' : f.type} {f.required ? '(required)' : '(optional)'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Template Grid */}
        {!selected && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="store-template-grid">
            {filtered.map(t => (
              <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-[#D4A843]/30 transition-all cursor-pointer group" onClick={() => setSelected(t)} data-testid={`store-template-${t.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-lg ${CATEGORY_COLORS[t.category] || 'bg-gray-100 text-gray-600'}`}>{t.category}</span>
                  <span className="text-[10px] text-gray-400 font-mono">SOW #{t.sow_number}</span>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-[#D4A843] transition-colors">{t.title}</h3>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">{t.description}</p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div>
                    <span className="text-xl font-bold text-gray-900">${t.pricing.basic}</span>
                    <span className="text-xs text-gray-400"> — ${t.pricing.premium}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#D4A843] transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-16 py-8 border-t border-gray-200">
          <p className="text-sm text-gray-500">TechOps DocForge — EIGOO Inc | eigooinc.net</p>
          <p className="text-[10px] text-gray-400 mt-1">Professional compliance & governance documentation powered by AI | <a href="https://app.emergent.sh/?via=Eigoo" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline">Powered by Emergent</a> | <a href="https://www.skool.com/signup?ref=e3e2967f8a3846bb87a53db8b592c54e" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline">Join our Skool</a></p>
        </div>
      </div>
    </div>
  );
}
